import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { client } from '../api/client'
import { CoParentForm } from '../components/CoParentForm'
import { NavigationBar } from '../components/NavigationBar'
import { useAuthStore } from '../stores/auth'
import { Parent } from '../types'

interface ProfileCompletionFormData {
  street_name: string
  street_number: string
  city: string
  zip_code: string
  vat_number: string
  social_security_number: string
}

export function ProfileCompletionPage() {
  const { t } = useTranslation(['profile'])
  const navigate = useNavigate()
  const { parent, updateParent } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showCoParentForm, setShowCoParentForm] = useState(false)
  const [coParent, setCoParent] = useState<Parent | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ProfileCompletionFormData>({
    defaultValues: {
      street_name: parent?.street_name || '',
      street_number: parent?.street_number || '',
      city: parent?.city || '',
      zip_code: parent?.zip_code || '',
      vat_number: parent?.vat_number || '',
      social_security_number: parent?.social_security_number || '',
    }
  })

  useEffect(() => {
    // If profile is already complete, redirect to home
    if (parent?.is_profile_complete) {
      navigate('/')
      return
    }
    
    // Set co-parent if available
    if (parent?.co_parent) {
      setCoParent(parent.co_parent)
      // Verify co-parent existence
      verifyCoParentExists(parent.co_parent.id);
    }
  }, [parent, navigate])

  // Function to verify if the co-parent still exists in the backend
  const verifyCoParentExists = async (coParentId: number) => {
    try {
      await client.get(`/co-parents/${coParentId}/`);
      // If the request succeeds, the co-parent exists
    } catch (error: any) {
      // If we get a 404, the co-parent doesn't exist anymore
      if (error.response && error.response.status === 404) {
        // Update local state
        setCoParent(null);
        // Update parent state to remove the co-parent
        if (parent) {
          updateParent({
            ...parent,
            co_parent: null
          });
        }
      }
    }
  }

  // Function to refresh profile data from the backend
  const refreshProfileData = async () => {
    try {
      setIsLoading(true);
      const response = await client.get('/profile/');
      updateParent(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error refreshing profile data:', error);
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: ProfileCompletionFormData) => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await client.patch('/profile/complete/', data)
      updateParent(response.data)
      navigate('/')
    } catch (err: unknown) {
      // Handle specific validation errors
      const error = err as any; // Type assertion for backward compatibility
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Handle VAT number errors
        if (errorData.vat_number) {
          if (errorData.vat_number[0].includes('9 digits')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatFormat', { ns: 'profile' }) 
            });
          } else if (errorData.vat_number[0].includes('check digit')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatInvalid', { ns: 'profile' }) 
            });
          } else if (errorData.vat_number[0].includes('already in use')) {
            setError('vat_number', { 
              type: 'manual', 
              message: t('validation.vatExists', { ns: 'profile' }) 
            });
          }
        }
        
        // Handle Social Security Number errors
        if (errorData.social_security_number) {
          if (errorData.social_security_number[0].includes('11 digits')) {
            setError('social_security_number', { 
              type: 'manual', 
              message: t('validation.ssnFormat', { ns: 'profile' }) 
            });
          } else if (errorData.social_security_number[0].includes('already in use')) {
            setError('social_security_number', { 
              type: 'manual', 
              message: t('validation.ssnExists', { ns: 'profile' }) 
            });
          }
        }
        
        setErrorMessage(t('errors.submit', { ns: 'profile' }))
      } else {
        setErrorMessage(t('errors.submit', { ns: 'profile' }))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddCoParent = () => {
    setShowCoParentForm(true)
  }

  const handleCoParentSuccess = (newCoParent: Parent) => {
    setCoParent(newCoParent)
    setShowCoParentForm(false)
    
    // Update parent with co-parent information
    if (parent) {
      updateParent({
        ...parent,
        co_parent: newCoParent
      })
    }
  }

  return (
    <div className="profile-completion-container" style={{ minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <NavigationBar />

      {/* Main Content */}
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="card-neobrutalism">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h1 className="text-2xl font-black text-neobrutalism-text">
                {t('completion.title', { ns: 'profile' })}
              </h1>
            </div>
          </div>
          
          <p className="mb-6 text-neobrutalism-text">
            {t('completion.description', { ns: 'profile' })}
          </p>

          {errorMessage && (
            <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
              <p className="text-neobrutalism-text font-bold">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* VAT Number */}
              <div>
                <label className="block font-bold mb-2" htmlFor="vat_number">
                  {t('fields.vatNumber', { ns: 'profile' })}
                  <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </label>
                <input
                  type="text"
                  id="vat_number"
                  {...register('vat_number', { 
                    required: t('validation.vatRequired', { ns: 'profile' }),
                    pattern: {
                      value: /^\d{9}$/,
                      message: t('validation.vatFormat', { ns: 'profile' })
                    }
                  })}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('fields.vatNumberPlaceholder', { ns: 'profile' })}
                />
                {errors.vat_number && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.vat_number.message}</p>
                )}
              </div>

              {/* Social Security Number */}
              <div>
                <label className="block font-bold mb-2" htmlFor="social_security_number">
                  {t('fields.socialSecurityNumber', { ns: 'profile' })}
                  <span className="text-gray-500 text-sm italic ml-1">{t('fields.parentSuffix', { ns: 'profile' })}</span>
                </label>
                <input
                  type="text"
                  id="social_security_number"
                  {...register('social_security_number', { 
                    required: t('validation.ssnRequired', { ns: 'profile' }),
                    pattern: {
                      value: /^\d{11}$/,
                      message: t('validation.ssnFormat', { ns: 'profile' })
                    }
                  })}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('fields.socialSecurityNumberPlaceholder', { ns: 'profile' })}
                />
                {errors.social_security_number && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.social_security_number.message}</p>
                )}
              </div>
            </div>

            {/* Address Section Header */}
            <div className="mt-4 mb-2">
              <h3 className="text-xl font-bold">{t('profile.edit.addressSection', { ns: 'profile' })}</h3>
            </div>

            {/* Address Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Street Name */}
              <div>
                <label className="block font-bold mb-2" htmlFor="street_name">
                  {t('fields.streetName', { ns: 'profile' })}
                </label>
                <input
                  type="text"
                  id="street_name"
                  {...register('street_name', { required: t('validation.streetNameRequired', { ns: 'profile' }) })}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('fields.streetNamePlaceholder', { ns: 'profile' })}
                />
                {errors.street_name && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.street_name.message}</p>
                )}
              </div>

              {/* Street Number */}
              <div>
                <label className="block font-bold mb-2" htmlFor="street_number">
                  {t('fields.streetNumber', { ns: 'profile' })}
                </label>
                <input
                  type="text"
                  id="street_number"
                  {...register('street_number', { required: t('validation.streetNumberRequired', { ns: 'profile' }) })}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('fields.streetNumberPlaceholder', { ns: 'profile' })}
                />
                {errors.street_number && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.street_number.message}</p>
                )}
              </div>

              {/* City */}
              <div>
                <label className="block font-bold mb-2" htmlFor="city">
                  {t('fields.city', { ns: 'profile' })}
                </label>
                <input
                  type="text"
                  id="city"
                  {...register('city', { required: t('validation.cityRequired', { ns: 'profile' }) })}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('fields.cityPlaceholder', { ns: 'profile' })}
                />
                {errors.city && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.city.message}</p>
                )}
              </div>

              {/* Zip Code */}
              <div>
                <label className="block font-bold mb-2" htmlFor="zip_code">
                  {t('fields.zipCode', { ns: 'profile' })}
                </label>
                <input
                  type="text"
                  id="zip_code"
                  {...register('zip_code', { required: t('validation.zipCodeRequired', { ns: 'profile' }) })}
                  className="w-full p-2 border-2 border-black"
                  placeholder={t('fields.zipCodePlaceholder', { ns: 'profile' })}
                />
                {errors.zip_code && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.zip_code.message}</p>
                )}
              </div>
            </div>

            {/* Co-Parent Section */}
            <div className="mt-8 mb-2">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">{t('coParent.section.title', { ns: 'profile' })}</h3>
                {!coParent && !showCoParentForm && (
                  <button
                    type="button"
                    onClick={handleAddCoParent}
                    className="px-4 py-2 bg-yellow-400 border-2 border-black font-bold flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    {t('coParent.actions.add', { ns: 'profile' })}
                  </button>
                )}
              </div>
              
              {!showCoParentForm && !coParent && (
                <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200 my-4 text-sm">
                  <div className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="mb-3">{t('coParent.info.description', { ns: 'profile' })}</p>
                      <p className="mb-3">
                        <span className="text-blue-600 mr-1">🔹</span>
                        <span className="font-semibold">{t('coParent.info.reminder', { ns: 'profile' })}</span>
                      </p>
                      <p>
                        <span className="text-red-600 mr-1">⚠️</span>
                        <span className="font-semibold">{t('coParent.info.important', { ns: 'profile' })}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {showCoParentForm && (
              <div>
                <CoParentForm 
                  existingCoParent={coParent}
                  onSuccess={handleCoParentSuccess} 
                  onRemove={coParent ? () => {
                    setCoParent(null);
                    setShowCoParentForm(false);
                    if (parent) {
                      updateParent({
                        ...parent,
                        co_parent: null
                      });
                    }
                  } : undefined}
                  isNested={true}
                  parent={parent}
                />
              </div>
            )}

            {coParent && !showCoParentForm && (
              <div className="bg-yellow-100 p-6 border-4 border-yellow-500">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-bold">{t('coParent.added.title', { ns: 'profile' })}</h4>
                  <button
                    type="button"
                    onClick={handleAddCoParent}
                    className="px-4 py-2 bg-yellow-500 border-2 border-black font-bold text-white"
                  >
                    {t('actions.edit', { ns: 'profile' })}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-bold">{t('fields.name', { ns: 'profile' })}</p>
                    <p>{coParent.first_name} {coParent.last_name}</p>
                  </div>
                  <div>
                    <p className="font-bold">{t('fields.email', { ns: 'profile' })}</p>
                    <p>{coParent.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end mt-8">
              <button
                type="submit"
                disabled={isLoading}
                className="btn-neobrutalism bg-neobrutalism-accent w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
              >
                {isLoading ? t('completion.submitting', { ns: 'profile' }) : t('completion.submit', { ns: 'profile' })}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
} 